// MealPlanHeader.js — Header that morphs from fullscreen intro to compact header
import React, { useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { lockBodyScroll } from "../../../utils/bodyScrollLock";

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DUR = '700ms';
const FADE_IN_DUR = '500ms';
const MORPH_MS = 700;

// Memoized Lottie — same avocado asset as RecipeCard preloader.
// Same size as RecipeCard (80×80). Uses the non-worker variant because it
// reliably loads the local .lottie file.
const IntroAvocado = memo(function IntroAvocado() {
  return (
    <DotLottieReact
      src="/assets/recipe-preloader.lottie"
      loop
      autoplay
      style={{ width: 80, height: 80 }}
    />
  );
});

/**
 * @param {Object} props
 * @param {string} props.currentDescription - Currently displayed rotating description
 * @param {Function} props.onOpenDetails - Open preferences drawer
 * @param {Function} props.onOpenFreshUp - Open fresh-up drawer
 * @param {boolean} props.introActive - When true, header renders as fullscreen yellow intro
 */
function MealPlanHeader({ currentDescription, onOpenDetails, onOpenFreshUp, introActive }) {
  const { t } = useTranslation('mealplan');

  // Mount fade-in: opacity + subtle rise from below.
  // Both properties are compositor-cheap (opacity + transform).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Keep Lottie mounted only while the intro is visible or morphing out.
  // After the morph finishes we unmount it so the worker stops consuming CPU.
  const [renderLottie, setRenderLottie] = useState(introActive);
  useEffect(() => {
    if (introActive) {
      setRenderLottie(true);
      return undefined;
    }
    const t = setTimeout(() => setRenderLottie(false), MORPH_MS);
    return () => clearTimeout(t);
  }, [introActive]);

  // Lock page scroll while the intro is active so the meal sections below stay hidden.
  useEffect(() => {
    if (!introActive) return undefined;
    return lockBodyScroll({ fixed: false });
  }, [introActive]);

  const containerTransition = `min-height ${DUR} ${EASE}, padding ${DUR} ${EASE}, background-color ${DUR} ${EASE}, opacity ${FADE_IN_DUR} ${EASE}, transform ${FADE_IN_DUR} ${EASE}`;

  return (
    <div
      className="relative overflow-hidden"
      style={{
        minHeight: introActive ? '100vh' : '0px',
        paddingTop: introActive ? '14vh' : '28px',
        paddingBottom: introActive ? '16px' : '16px',
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translate3d(0, 0, 0)' : 'translate3d(0, 24px, 0)',
        willChange: 'transform, opacity',
        transition: containerTransition,
      }}
    >
      {/* Yellow gradient background with hero-bg texture — fades out */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundColor: '#FFCB00',
          backgroundImage:
            'linear-gradient(180deg, rgba(255, 203, 0, 0.2) 0%, rgba(255, 203, 0, 0.4) 48%, rgba(255, 203, 0, 0.6) 100%), url("/assets/landing/hero-bg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'top center',
          opacity: introActive ? 1 : 0,
          transition: `opacity ${DUR} ${EASE}`,
        }}
      />

      <div className="relative px-6 max-w-5xl mx-auto">
        {/* Avocado Lottie — visible only during intro, collapses with the rest */}
        <div
          className="overflow-hidden flex justify-start"
          style={{
            maxHeight: introActive ? '100px' : '0px',
            opacity: introActive ? 1 : 0,
            marginBottom: introActive ? '8px' : '0px',
            marginLeft: introActive ? '-20px' : '0px',
            transform: introActive ? 'translateY(0)' : 'translateY(-12px)',
            transition: `max-height ${DUR} ${EASE}, opacity ${DUR} ${EASE}, margin-bottom ${DUR} ${EASE}, transform ${DUR} ${EASE}`,
            contain: 'layout paint style',
            willChange: 'transform, opacity',
          }}
        >
          {renderLottie && <IntroAvocado />}
        </div>

        {/* Title — same h1 "Review Meals" morphs from 36px to 24px */}
        <h1
          className="font-bold text-[#0D0C07]"
          style={{
            fontSize: introActive ? '36px' : '24px',
            lineHeight: introActive ? '60px' : '32px',
            letterSpacing: introActive ? '-0.23px' : '0',
            marginBottom: introActive ? '20px' : '4px',
            transition: `font-size ${DUR} ${EASE}, line-height ${DUR} ${EASE}, letter-spacing ${DUR} ${EASE}, margin-bottom ${DUR} ${EASE}`,
          }}
        >
          {t('reviewMeals')}
          {/* Second title line — "& Groceries" / "и продукты" — collapses */}
          <span
            className="block overflow-hidden"
            style={{
              maxHeight: introActive ? '80px' : '0px',
              opacity: introActive ? 1 : 0,
              transition: `max-height ${DUR} ${EASE}, opacity ${DUR} ${EASE}`,
            }}
          >
            {t('intro.titleLine2')}
          </span>
        </h1>

        {/* Intro body (description + closing lines) — collapses */}
        <div
          className="overflow-hidden"
          style={{
            maxHeight: introActive ? '500px' : '0px',
            opacity: introActive ? 1 : 0,
            marginBottom: introActive ? '24px' : '0px',
            transition: `max-height ${DUR} ${EASE}, opacity ${DUR} ${EASE}, margin-bottom ${DUR} ${EASE}`,
          }}
        >
          <p className="text-[18.8px] leading-[36px] text-[#0D0C07] mb-5">
            {t('intro.description')}
          </p>
          <p className="text-[18.8px] leading-[36px] font-bold text-[#0D0C07]">
            {t('intro.closing1')}
            <br />
            {t('intro.closing2')}
            <br />
            {t('intro.closing3')}
          </p>
        </div>

        {/* Rotating description — fades in after intro */}
        <div
          className="overflow-hidden"
          style={{
            maxHeight: introActive ? '0px' : '80px',
            opacity: introActive ? 0 : 1,
            marginBottom: introActive ? '0px' : '16px',
            transition: `max-height ${DUR} ${EASE}, opacity ${DUR} ${EASE}, margin-bottom ${DUR} ${EASE}`,
          }}
        >
          <p className="font-medium text-[#3F3C31] text-[15.2px] leading-[28px] transition-opacity duration-500">
            {currentDescription || ''}
          </p>
        </div>

        {/* Tags — fade in + subtle rise */}
        <div
          className="flex gap-2 mb-1"
          style={{
            opacity: introActive ? 0 : 1,
            transform: introActive ? 'translateY(6px)' : 'translateY(0)',
            pointerEvents: introActive ? 'none' : 'auto',
            transition: `opacity ${DUR} ${EASE}, transform ${DUR} ${EASE}`,
          }}
        >
        {/* Details Button */}
        <span
          onClick={onOpenDetails}
          className={`inline-flex items-center
              pl-2 pr-3 py-1
              text-sm font-medium
              rounded-[10px]
              border-[0.5px] border-[#F3F3F2]
              bg-[rgba(255,255,255,0.64)]
              shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
              backdrop-blur-lg
              cursor-pointer
              md:hover:bg-[rgba(255,255,255,0.8)]
              transition-colors`}
        >
          <span className="mr-1 w-[24px]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M20 3.00001V7.00001M22 5.00001H18M4 17V19M5 18H3M9.937 15.5C9.84772 15.1539 9.66734 14.8381 9.41462 14.5854C9.1619 14.3327 8.84607 14.1523 8.5 14.063L2.365 12.481C2.26033 12.4513 2.16821 12.3883 2.10261 12.3014C2.03702 12.2146 2.00153 12.1088 2.00153 12C2.00153 11.8912 2.03702 11.7854 2.10261 11.6986C2.16821 11.6118 2.26033 11.5487 2.365 11.519L8.5 9.93601C8.84595 9.84681 9.16169 9.66658 9.4144 9.41404C9.66711 9.16151 9.84757 8.84589 9.937 8.50001L11.519 2.36501C11.5484 2.25992 11.6114 2.16735 11.6983 2.1014C11.7853 2.03545 11.8914 1.99976 12.0005 1.99976C12.1096 1.99976 12.2157 2.03545 12.3027 2.1014C12.3896 2.16735 12.4526 2.25992 12.482 2.36501L14.063 8.50001C14.1523 8.84608 14.3327 9.1619 14.5854 9.41462C14.8381 9.66734 15.1539 9.84773 15.5 9.93701L21.635 11.518C21.7405 11.5471 21.8335 11.61 21.8998 11.6971C21.9661 11.7841 22.0021 11.8906 22.0021 12C22.0021 12.1094 21.9661 12.2159 21.8998 12.3029C21.8335 12.39 21.7405 12.4529 21.635 12.482L15.5 14.063C15.1539 14.1523 14.8381 14.3327 14.5854 14.5854C14.3327 14.8381 14.1523 15.1539 14.063 15.5L12.481 21.635C12.4516 21.7401 12.3886 21.8327 12.3017 21.8986C12.2147 21.9646 12.1086 22.0003 11.9995 22.0003C11.8904 22.0003 11.7843 21.9646 11.6973 21.8986C11.6104 21.8327 11.5474 21.7401 11.518 21.635L9.937 15.5Z"
                stroke="black"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="ml-0 font-normal">{t('myPreferences')}</span>
        </span>
        <span
          onClick={onOpenFreshUp}
          className="inline-flex items-center
              px-3 pl-2 py-1
              text-sm font-medium
              rounded-[10px]
              cursor-pointer
              border-[0.5px] border-[#F3F3F2]
              bg-[rgba(255,255,255,0.64)]
              shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
              backdrop-blur-lg"
        >
          {/*<span className="mr-1 w-[24px]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M4.45858 20.3335V16.1665C4.45858 15.6833 4.85033 15.2915 5.33358 15.2915H9.50057C9.98367 15.2917 10.3756 15.6834 10.3756 16.1665C10.3756 16.6496 9.98367 17.0413 9.50057 17.0415H7.07283L8.16268 18.0103L8.43221 18.2339C9.07664 18.7363 9.82143 19.0978 10.6178 19.2925C11.5277 19.5149 12.4781 19.5131 13.3873 19.2876C14.2965 19.0621 15.1379 18.6195 15.8385 17.9976C16.5389 17.3757 17.0781 16.5932 17.4097 15.7173C17.7414 14.8411 17.8558 13.8973 17.7428 12.9673C17.6297 12.0373 17.2923 11.1486 16.7603 10.3774C16.2283 9.60637 15.5173 8.97544 14.6881 8.53955C13.8589 8.10373 12.9363 7.87555 11.9996 7.87451H11.9937C10.5753 7.86339 9.2019 8.37401 8.13436 9.30811C7.06688 10.2422 6.379 11.5355 6.20174 12.9429C6.14118 13.4221 5.70354 13.762 5.2242 13.7017C4.74484 13.6413 4.4052 13.2035 4.46541 12.7241C4.69625 10.8915 5.59191 9.20707 6.98201 7.99072C8.37047 6.77586 10.1559 6.11165 12.0006 6.12451C13.2205 6.12581 14.4227 6.4231 15.5025 6.99072C16.5823 7.55834 17.508 8.38018 18.2008 9.38428C18.8934 10.3883 19.3328 11.5455 19.4801 12.7563C19.6273 13.9672 19.4783 15.1957 19.0465 16.3364C18.6146 17.4773 17.9119 18.4963 16.9996 19.3062C16.0873 20.116 14.9922 20.6932 13.8082 20.9868C12.6242 21.2804 11.3866 21.2824 10.2017 20.9927C9.01675 20.7029 7.91941 20.1296 7.00447 19.3228L7.00252 19.3208L6.20858 18.6147V20.3335C6.2084 20.8166 5.81672 21.2085 5.33358 21.2085C4.85044 21.2085 4.45875 20.8166 4.45858 20.3335ZM11.1256 13.6665V10.3335C11.1256 9.85025 11.5173 9.4585 12.0006 9.4585C12.4837 9.45867 12.8756 9.85036 12.8756 10.3335V13.6665C12.8756 14.1496 12.4837 14.5413 12.0006 14.5415C11.5173 14.5415 11.1256 14.1498 11.1256 13.6665ZM13.6666 2.7915C14.1498 2.7915 14.5416 3.18325 14.5416 3.6665C14.5416 4.14975 14.1498 4.5415 13.6666 4.5415H10.3336C9.85033 4.5415 9.45858 4.14975 9.45858 3.6665C9.45858 3.18325 9.85033 2.7915 10.3336 2.7915H13.6666Z"
                fill="#0D0C07"
              />
            </svg>
          </span>*/}
          <span className="ml-0 font-normal">{t('tips.whyNineMeals')}</span>
        </span>
      </div>
      </div>
    </div>
  );
}

export default MealPlanHeader;
